import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetchJson } from '../../apiClient';
import './SessionView.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

function SessionView({ instance, session, onBack, onUpdate, showMessage }) {
  const { t } = useTranslation();
  const [sessionDetail, setSessionDetail] = useState(null);
  const [wines, setWines] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadSession = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiFetchJson(instance, `/api/tasting/sessions/${session.id}`);
      setSessionDetail(data);
      setWines(unwrap(data.wines || data.sessionWines));
      setParticipants(unwrap(data.participants));
      setNotes(unwrap(data.tastingNotes || data.notes));
    } catch (e) {
      console.error('Failed to load session:', e);
      setError(t('grapeEscape.session.loadError', 'Failed to load session details.'));
    } finally {
      setLoading(false);
    }
  }, [instance, session.id, t]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const handleRevealWine = useCallback(async (wineId) => {
    try {
      await apiFetchJson(instance, `/api/tasting/sessions/${session.id}/wines/${wineId}/reveal`, {
        method: 'POST',
      });
      loadSession();
      showMessage(t('grapeEscape.session.wineRevealed', 'Wine revealed! 🍷'));
    } catch (e) {
      console.error('Failed to reveal wine:', e);
    }
  }, [instance, session.id, loadSession, showMessage, t]);

  const handleComplete = useCallback(async () => {
    try {
      await apiFetchJson(instance, `/api/tasting/sessions/${session.id}/complete`, {
        method: 'POST',
      });
      loadSession();
      onUpdate();
      showMessage(t('grapeEscape.session.completed', 'Session completed! 🎉'));
    } catch (e) {
      console.error('Failed to complete session:', e);
    }
  }, [instance, session.id, loadSession, onUpdate, showMessage, t]);

  if (loading) {
    return (
      <div className="ge-loading">
        <div className="ge-spinner" />
        <span>{t('grapeEscape.session.loading', 'Loading session...')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ge-error">
        <span>{error}</span>
        <button onClick={loadSession}>↻</button>
      </div>
    );
  }

  const detail = sessionDetail || session;

  return (
    <div className="ge-session-detail">
      <div className="ge-section-header">
        <button className="ge-btn-secondary" onClick={onBack}>
          ← {t('grapeEscape.session.back', 'Back')}
        </button>
        <h3>{detail.name}</h3>
      </div>

      {/* Session Info */}
      <div className="ge-session-info ge-card">
        <div className="ge-session-info-grid">
          <div>
            <span className="ge-info-label">{t('grapeEscape.session.type', 'Type')}</span>
            <span className="ge-info-value">{detail.type || 'Open'}</span>
          </div>
          <div>
            <span className="ge-info-label">{t('grapeEscape.session.date', 'Date')}</span>
            <span className="ge-info-value">
              {detail.date ? new Date(detail.date).toLocaleDateString() : '—'}
            </span>
          </div>
          <div>
            <span className="ge-info-label">{t('grapeEscape.session.status', 'Status')}</span>
            <span className={`ge-session-status ${(detail.status || '').toLowerCase()}`}>
              {detail.status || 'Draft'}
            </span>
          </div>
          <div>
            <span className="ge-info-label">{t('grapeEscape.session.blind', 'Blind')}</span>
            <span className="ge-info-value">{detail.isBlind ? '🙈 Yes' : 'No'}</span>
          </div>
        </div>
      </div>

      {/* Wines */}
      <div className="ge-section">
        <div className="ge-section-header">
          <h3>{t('grapeEscape.session.wines', 'Wines')} ({wines.length})</h3>
        </div>
        {wines.length === 0 ? (
          <div className="ge-empty">{t('grapeEscape.session.noWines', 'No wines added yet.')}</div>
        ) : (
          <div className="ge-session-wines">
            {wines.map((wine, idx) => (
              <div key={wine.id || idx} className="ge-session-wine-card ge-card">
                <span className="ge-wine-number">#{idx + 1}</span>
                <div className="ge-session-wine-info">
                  {detail.isBlind && !wine.revealed ? (
                    <span className="ge-blind-label">{t('grapeEscape.session.hidden', 'Hidden')}</span>
                  ) : (
                    <span className="ge-wine-name">{wine.wineName || wine.name || 'Unknown'}</span>
                  )}
                </div>
                {detail.isBlind && !wine.revealed && detail.status !== 'Completed' && (
                  <button
                    className="ge-btn-secondary"
                    onClick={() => handleRevealWine(wine.id)}
                  >
                    {t('grapeEscape.session.reveal', 'Reveal')}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Participants */}
      <div className="ge-section">
        <div className="ge-section-header">
          <h3>{t('grapeEscape.session.participants', 'Participants')} ({participants.length})</h3>
        </div>
        {participants.length === 0 ? (
          <div className="ge-empty">{t('grapeEscape.session.noParticipants', 'No participants yet.')}</div>
        ) : (
          <div className="ge-participants-list">
            {participants.map((p, idx) => (
              <span key={p.id || idx} className="ge-participant-chip">
                {p.name || p.userName || 'Guest'}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Tasting Notes Comparison */}
      {notes.length > 0 && (
        <div className="ge-section">
          <h3>{t('grapeEscape.session.notesComparison', 'Tasting Notes')}</h3>
          <div className="ge-comparison-grid">
            {notes.map((note, idx) => (
              <div key={note.id || idx} className="ge-comparison-card ge-card">
                <div className="ge-comparison-header">
                  <span className="ge-note-wine-name">{note.wineName || `Wine #${idx + 1}`}</span>
                  <span className="ge-note-author">{note.authorName || ''}</span>
                </div>
                <div className="ge-comparison-details">
                  {note.rating && <span>Rating: {'⭐'.repeat(note.rating)}</span>}
                  {note.qualityLevel && <span>Quality: {note.qualityLevel}</span>}
                </div>
                {note.notes && <p className="ge-comparison-notes">{note.notes}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {detail.status !== 'Completed' && (
        <div className="ge-session-actions">
          <button className="ge-btn-primary" onClick={handleComplete}>
            ✓ {t('grapeEscape.session.complete', 'Complete Session')}
          </button>
        </div>
      )}
    </div>
  );
}

export default SessionView;
